import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { ReactNode } from 'react';
import { useGetEffortByIdQuery, useUpdateEffortMutation } from 'store/api/efforts';
import { ClassifierData } from 'types/classifiers';
import { Effort, EffortForm, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { useClassifiers } from 'utils/useClassifiers';
import {
    EFFORT_FORM_CLASSIFIERS,
    EFFORT_SECTION_FIELDS,
    EffortSectionId,
    effortDtoToForm,
    effortSectionToRequest,
    joinAddedPeople,
} from '../effortFormMapping';
import { ExpensesFields, LinkedEntitiesFields, OverviewFields, PeopleFields } from './EffortFields';
import { NominationsFields } from './NominationsFields';
import { EffortFormState, useEffortForm } from './useEffortForm';

const SECTION_FIELDS: Record<EffortSectionId, (form: EffortFormState) => ReactNode> = {
    overview: (form) => (
        <>
            <OverviewFields form={form} />
            {/* Changed to Rankings referee here, the type brings its lines along: it is saved with one. */}
            {form.showsNominations &&
            form.methods.formState.defaultValues?.effortActivityType?.value !== RANKINGS_REFEREE_TYPE ? (
                <NominationsFields form={form} />
            ) : null}
        </>
    ),
    'linked-entities': (form) => <LinkedEntitiesFields form={form} />,
    people: (form) => <PeopleFields form={form} />,
    expenses: (form) => <ExpensesFields form={form} />,
    nominations: (form) => <NominationsFields form={form} />,
};

interface SectionFormProps {
    section: EffortSectionId;
    effort: Effort;
    initialData: EffortForm;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function EffortSectionForm({ section, effort, initialData, classifiers, onClose }: SectionFormProps) {
    const form = useEffortForm({ initialData, classifiers, expenses: initialData.expenses });
    // The page's argument (the route's id), so the save refreshes the page's own cache entry.
    const { refetch } = useGetEffortByIdQuery(String(effort.id));
    const [updateEffort] = useUpdateEffortMutation();

    const onSave = (values: EffortForm) =>
        saveSection(values, {
            section,
            sectionFields: EFFORT_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => effortDtoToForm(dto, classifiers),
            // A changed type takes the lines its section edited: started for Rankings referee, emptied for another.
            // People added join the lines as stored, as the form joins them.
            toRequest: (edited, latest) => ({
                ...effortSectionToRequest(
                    section,
                    edited.effortActivityType?.value !== latest.effortActivityType?.value
                        ? { ...edited, nominations: values.nominations }
                        : form.showsNominations
                          ? { ...edited, nominations: joinAddedPeople(edited, latest) }
                          : edited
                ),
                id: String(effort.id),
            }),
            update: (request) => updateEffort(request).unwrap(),
            onSaved: onClose,
        });

    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose} formKey="bd-effort-edit">
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: EffortSectionId;
    effort: Effort;
    onClose: () => void;
}

function EffortSectionEditor({ section, effort, onClose }: EditorProps) {
    const { classifiers, isLoading } = useClassifiers({ codes: EFFORT_FORM_CLASSIFIERS });

    const initialData = useFrozenOnceLoaded(!isLoading, () => effortDtoToForm(effort, classifiers));

    if (!initialData) {
        return <Loader />;
    }
    return (
        <EffortSectionForm
            section={section}
            effort={effort}
            initialData={initialData}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface EffortSectionProps {
    id: EffortSectionId;
    titleId: string;
    effort: Effort;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
}

/** A BD effort section on the record page that edits in place with the effort form's fields for that section. */
export function EffortSection({ id, titleId, effort, children }: EffortSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <EffortSectionEditor section={id} effort={effort} onClose={close} />}
        >
            {children}
        </SectionEdit>
    );
}
